import { bodyFonts, elevation } from "@DSG/MobiusOverrides/styles/variables";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Img from "@insite/mobius/Img/Img";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect } from "react-redux";
import styled, { css } from "styled-components";


// this is used to help ensure that the names match between props.fields and definition.fieldDefinitions[].name
const enum fields {
    showIconImage = "showIconImage",
    iconImage = "iconImage",
    iconImageAltText = "iconImageAltText",
    messageBorderColor = "messageBorderColor",
    messageTextColor = "messageTextColor",
    messageBackgroundColor = "messageBackgroundColor",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showIconImage]: boolean;
        [fields.iconImage]: string;
        [fields.iconImageAltText]: string;
        [fields.messageBorderColor]: string;
        [fields.messageTextColor]: string;
        [fields.messageBackgroundColor]: string;
    };
}

export interface DsgPreviouslyPurchasedStyles {
    wrapper?: InjectableCss;
    messageContent?: InjectableCss;
    lastPurchasedText?: TypographyProps;
    qtyText?: TypographyProps;
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        isAuthenticated: state.context?.session?.isAuthenticated && !state.context?.session?.isGuest,
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasProduct;



export const dsgPreviouslyPurchasedStyles: DsgPreviouslyPurchasedStyles = {
    wrapper: {
        css: css`
            margin-bottom: 24px;
            img {
                width: 24px;
                height: auto;
                min-width: 24px;
            }
            > div {
                display: flex;
                flex-wrap: nowrap;
                column-gap: 8px;
                align-items: center;
            }
        `
    },
    messageContent: {
        css: css`
            display: flex;
            flex-direction: column;
        `
    },
    lastPurchasedText: {
        css: css`
            font: ${bodyFonts.body2SemiBoldLg.fontWeight} ${bodyFonts.body2SemiBoldLg.fontSize}/${bodyFonts.body2SemiBoldLg.lineHeight} ${bodyFonts.body2SemiBoldLg.fontFamily};
        `
    },
    qtyText: {
        css: css`
            font: ${bodyFonts.body2RegularLg.fontWeight} ${bodyFonts.body2RegularLg.fontSize}/${bodyFonts.body2RegularLg.lineHeight} ${bodyFonts.body2RegularLg.fontFamily};
        `
    },
}

const styles = dsgPreviouslyPurchasedStyles;

const Wrapper = styled(StyledWrapper) <{ borderColor: string; textColor: string; bgColor: string }>`
border: 1px solid ${({ borderColor }) => borderColor};
    border-left-width: 8px;
    padding: 18px 24px;
    background-color: ${({ bgColor }) => bgColor};
    border-radius: 4px;
    box-shadow: ${elevation.dp08};
    .lastPurchasedText,
    .qtyText {
        color: ${({ textColor }) => textColor};
    }
`;

const DsgPreviouslyPurchased: React.FC<Props> = ({
    fields,
    isAuthenticated,
    product

}) => {

    return (
    <>
    {isAuthenticated && product.properties["previouslyPurchaseDate"] && product.properties["previouslyPurchaseQty"] &&  (
        <StyledWrapper {...styles.wrapper}>
            <Wrapper
                borderColor={fields.messageBorderColor}
                textColor={fields.messageTextColor}
                bgColor={fields.messageBackgroundColor}>
                {fields.showIconImage && fields.iconImage && (
                    <Img src={fields.iconImage} altText={fields.iconImageAltText} />
                )}
                <StyledWrapper {...styles.messageContent}>
                    <Typography className="lastPurchasedText" {...styles.lastPurchasedText}>
                        {translate('Last purchased ')} {product.properties["previouslyPurchaseDate"]}
                    </Typography>
                    <Typography className="qtyText" {...styles.qtyText}>
                        {translate('Qty: ')} {product.properties["previouslyPurchaseQty"]}                        
                    </Typography>
                </StyledWrapper>
            </Wrapper>
        </StyledWrapper>
    )}
    </>
    )
};



const widgetModule: WidgetModule = {
    component: withProduct(connect(mapStateToProps)(DsgPreviouslyPurchased)),
    //component: DsgPreviouslyPurchased,
    definition: {
        group: "Common",
        fieldDefinitions: [
            {
                fieldType: "General",
                displayName: "Show Icon Image",
                name: fields.showIconImage,
                editorTemplate: "CheckboxField",
                defaultValue: true,
            },
            {
                name: fields.iconImage,
                displayName: "Icon Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "/userfiles/info.png",
                fieldType: "Contextual",
                isVisible: widget => widget.fields.showIconImage,
            },
            {
                name: fields.iconImageAltText,
                editorTemplate: "TextField",
                displayName: "Icon Alt Text",
                defaultValue: "",
                fieldType: "General",
                isVisible: widget => widget.fields.showIconImage,
            },
            {
                name: fields.messageBorderColor,
                editorTemplate: "ColorPickerField",
                displayName: "Message Border Color",
                defaultValue: "#4083CA",
                fieldType: "General",
            },
            {
                name: fields.messageTextColor,
                editorTemplate: "ColorPickerField",
                displayName: "Message Text Color",
                defaultValue: "#1B1C1C",
                fieldType: "General",
            },
            {
                name: fields.messageBackgroundColor,
                editorTemplate: "ColorPickerField",
                displayName: "Message Background Color",
                defaultValue: "#FFFFFF",
                fieldType: "General",
            }
        ],
    }
};


export default widgetModule;
